<template>
  <InputTextBasic
    :id="id"
    v-model="value"
    :name="name"
    :label="label"
    :type="type"
    :rule="rule"
    :mask="mask"
    :disabled="disabled"
    :icon="icon"
    :text="text"
    :hide-error-message="hideErrorMessage"
    :input-size="inputSize"
    :autocomplete="autocomplete"
    :loading="loading"
    :text-type="textType"
    :combo-box-type="comboBoxType"
    :combo-box-data="comboBoxData"
    :class="{
      'is-invalid-input': errorMessage && delayErrorMsg,
      'is-input-confirmed': confirm,
    }"
    :is-invalid="(confirm || errorMessage) && !meta.valid"
    v-bind="$attrs"
    @change="handleChange"
    @blur="handleBlur"
    @focus="handleFocus"
    @onTextClick="handleTextClick"
    @onEnter="handleEnterAction"
    @onIconClick="handleOnIconClick"
  >
    <template #customIcon>
      <slot name="customIcon" />
    </template>
  </InputTextBasic>
  <!-- @blur="handleBlur" -->

  <div
    v-show="confirm"
    :id="id || confirmStateMessageId"
    class="show-for-sr"
    aria-live="polite"
    aria-atomic="true"
  >
    <span>{{ confirm && meta.dirty ? 'Valid Input Field' : '' }}</span>
  </div>
  <div
    :id="ariaErrorId"
    aria-live="polite"
    aria-atomic="true"
    :class="['form-error', {'is-visible': errorMessage && !hideErrorMessage && delayErrorMsg}]"
  >
    <Icon
      role="alert"
      :icon="csgErrorRed"
      :aria-label="errorMessage ? `Error ${errorMessage}`:''"
      :aria-hidden="!errorMessage && hideErrorMessage && delayErrorMsg"
    />
    <span> {{ errorMessage }} </span>
  </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { computed, watch } from 'vue';
import { csgErrorRed } from '@csgSvgIcons/icons';
import InputTextBasic from '../inputTextBasic';
import generateRandomId from '../../utils/randomNumber';
import Icon from '../icon';

const props = defineProps({
  id: {
    type: [String, Number],
    default: () => generateRandomId(),
  },
  name: {
    type: String,
    required: true,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  rule: {
    type: String,
    default: '',
  },
  mask: {
    type: [String],
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  text: {
    type: String,
    default: '',
  },
  textType: {
    type: String,
    default: undefined,
    validator: (value) => value && ['primary'].includes(value),
  },
  hideErrorMessage: {
    type: Boolean,
    default: false,
  },
  inputSize: {
    type: String,
    default: 'large',
  },
  autocomplete: {
    type: String,
    default: 'on',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  confirm: {
    type: Boolean,
    default: false,
  },
  comboBoxData: {
    type: Array,
    default: () => [],
  },
  comboBoxType: {
    type: String,
    default: undefined,
    validator: (val) => ['location', 'searchHistory'].includes(val),
  },
});
const emit = defineEmits(['blur', 'focus', 'onTextClick', 'onEnter', 'enterAction', 'onIconClick', 'update:modelValue']);

const name = computed(() => props.name);
const {
  value, errorMessage, handleChange, validate, meta,
} = useField(
  name, props.rule,
  {
    initialValue: props.modelValue,
    validateOnValueUpdate: false,
  },
);

const hideErrorMessage = false;
const delayErrorMsg = true;
const ariaErrorId = generateRandomId();
const confirmStateMessageId = generateRandomId();

watch(value, () => {
  emit('update:modelValue', value.value);
});

watch(() => props.modelValue, (newValue) => {
  value.value = newValue;
  validate();
});

const handleBlur = (event) => {
  validate();
  emit('blur', event);
};
const handleFocus = (event) => {
  emit('focus', event);
};
const handleTextClick = (event) => {
  emit('onTextClick', event);
};
const handleEnterAction = (event) => {
  emit('enterAction', event);
};
const handleOnIconClick = (event) => {
  emit('onIconClick', event);
};
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;
</style>
